import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientHistoryItem, NoteVisibility } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useClientHistory } from './api';

type Props = {
  clientId: number;
};

const PAGE_SIZE = 10;

export function ClientHistoryTab({ clientId }: Props): ReactNode {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce 300 ms — zgodnie z konwencją z F5 (lista klientów).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, error, isFetching } = useClientHistory(clientId, {
    search: search || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <input
          type="search"
          className="input max-w-md"
          placeholder={t('history.searchPlaceholder') ?? ''}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {isFetching && (
          <span className="text-xs text-zinc-400">{t('common.loading')}</span>
        )}
      </div>

      {isLoading && (
        <div className="rounded-xl bg-white p-6 text-zinc-500 shadow-sm ring-1 ring-zinc-200">
          {t('common.loading')}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {extractApiError(error)}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl bg-white p-8 text-center text-zinc-500 shadow-sm ring-1 ring-zinc-200">
          {search ? t('history.noResults') : t('history.empty')}
        </div>
      )}

      <ul className="space-y-3">
        {items.map((a) => (
          <HistoryItem key={a.id} appointment={a} />
        ))}
      </ul>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-zinc-500">
            {t('history.pageOf', { page, total: lastPage })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('common.prev')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryItem({
  appointment: a,
}: {
  appointment: ClientHistoryItem;
}): ReactNode {
  const { t } = useTranslation();
  const start = new Date(a.startAt);
  const dateLabel = start.toLocaleString('pl-PL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <li className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-900">{dateLabel}</div>
          <div className="text-xs text-zinc-500">
            {a.employee.firstName} {a.employee.lastName} · {a.totalDuration} min ·{' '}
            {a.totalPrice} zł
          </div>
        </div>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium uppercase ${statusBadgeClass(
            a.status,
          )}`}
        >
          {t(`appointments.status.${a.status}`)}
        </span>
      </header>

      {a.services.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5 text-xs text-zinc-600">
          {a.services.map((s) => (
            <li
              key={s.id}
              className="rounded bg-zinc-100 px-2 py-0.5"
              title={`${s.durationMinutes}m · ${s.price} zł`}
            >
              {s.name}
            </li>
          ))}
        </ul>
      )}

      {a.notes.length > 0 && (
        <ul className="mt-3 space-y-2 border-t border-zinc-100 pt-3">
          {a.notes.map((n) => (
            <li key={n.id} className="text-sm">
              <div className="mb-0.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span className="font-medium text-zinc-700">
                  {n.author.firstName} {n.author.lastName}
                </span>
                <span>·</span>
                <span>{new Date(n.createdAt).toLocaleString('pl-PL')}</span>
                <VisibilityBadge visibility={n.visibility} />
              </div>
              <p className="whitespace-pre-wrap text-zinc-800">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function VisibilityBadge({
  visibility,
}: {
  visibility: NoteVisibility;
}): ReactNode {
  const { t } = useTranslation();
  const isTeam = visibility === 'TEAM';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        isTeam
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-zinc-200 text-zinc-700'
      }`}
    >
      {isTeam ? t('notes.visibility.TEAM') : t('notes.visibility.EMPLOYEE')}
    </span>
  );
}

function statusBadgeClass(status: ClientHistoryItem['status']): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-emerald-100 text-emerald-700';
    case 'CANCELLED':
      return 'bg-zinc-200 text-zinc-600 line-through';
    case 'NO_SHOW':
      return 'bg-amber-100 text-amber-700';
    case 'CONFIRMED':
      return 'bg-blue-100 text-blue-700';
    case 'BOOKED':
    default:
      return 'bg-brand-100 text-brand-700';
  }
}
